/**
 * What the UI shows about Sync (issue #25, read by the screens of #26): one small observable
 * store the engine writes and the home screen, the header and the Account screen read. The same
 * shape as `src/pwa/status.ts`, but created per engine so two Devices can run in one test.
 */
import type { SyncErrorCode } from "../../shared/sync-format";

export type SyncStatus =
  /** This Device is not signed in. Everything works; nothing leaves it. */
  | { phase: "off" }
  | { phase: "syncing"; pending: number }
  /** Everything the Account and this Device hold is the same, as of `at`. */
  | { phase: "idle"; at: number; pending: number; conflicts: number; parked: number }
  /** The Worker could not be reached; what is pending goes out at the next connection. */
  | { phase: "offline"; at: number | null; pending: number }
  /** Stopped until the person acts: the Device was removed from the Account, or a ceiling was hit. */
  | { phase: "blocked"; code: SyncErrorCode; message: string; pending: number }
  /** Something else went wrong; the next trigger tries again. */
  | { phase: "failed"; message: string; at: number; pending: number };

type Listener = (status: SyncStatus) => void;

export interface SyncStatusStore {
  get(): SyncStatus;
  set(status: SyncStatus): void;
  subscribe(listener: Listener): () => void;
}

export function createSyncStatus(initial: SyncStatus = { phase: "off" }): SyncStatusStore {
  let current = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => current,
    set(status) {
      current = status;
      for (const listener of [...listeners]) listener(status);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** "3 changes" / "1 change", for the lines that count what is waiting. */
function changes(n: number): string {
  return `${n} ${n === 1 ? "change" : "changes"}`;
}

/** "14:02" in the Device's own time. */
function clock(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** One line for a screen or a log: what Sync is doing, and how far behind it is. */
export function describeSyncStatus(status: SyncStatus): string {
  switch (status.phase) {
    case "off":
      return "Not signed in";
    case "syncing":
      return status.pending > 0 ? `Syncing… ${changes(status.pending)} to send` : "Syncing…";
    case "idle": {
      const notes = [];
      if (status.conflicts > 0) notes.push(`${status.conflicts} conflict ${status.conflicts === 1 ? "copy" : "copies"}`);
      if (status.parked > 0) notes.push(`${status.parked} waiting to apply`);
      return `Synced ${clock(status.at)}${notes.length > 0 ? ` · ${notes.join(", ")}` : ""}`;
    }
    case "offline":
      return status.pending > 0
        ? `Offline, ${changes(status.pending)} waiting`
        : `Offline${status.at === null ? "" : `, last synced ${clock(status.at)}`}`;
    case "blocked":
      return status.message;
    case "failed":
      return `Sync failed: ${status.message}`;
  }
}

/**
 * What the person can do about the state Sync is in, or null when there is nothing to do. Every
 * refusal that stops the engine (`src/sync/client.ts`, `BLOCKING`) has an answer here, so a
 * screen never shows a code without a way out (#26).
 */
export function describeSyncFix(status: SyncStatus): string | null {
  if (status.phase === "offline") {
    return status.pending > 0 ? "Everything waiting goes out by itself at the next connection." : null;
  }
  if (status.phase === "failed") return "The next launch, the next save, or Sync now tries again.";
  if (status.phase !== "blocked") return null;
  switch (status.code) {
    case "unauthorized":
      return "This device was signed out of the Account, or removed from it. Sign in again to carry on syncing; nothing on this device is lost.";
    case "account_document_limit":
    case "account_byte_limit":
      return "The Account is full. Delete Characters you no longer need on any Device — they go to Recently Deleted — then Sync now.";
    case "document_too_large":
    case "document_not_storable":
      return "One Character is too big to sync. Export it to a file, delete some of what it carries (a long Roll Log, say), and Sync now.";
    case "server_misconfigured":
      return "The Sync Worker is not fully set up, so nothing can sync. This device keeps working; try again later.";
    default:
      return "Sync now to try again.";
  }
}

/** The one-line indicator on the home header and in the Character menu (#26); null when there is nothing to say. */
export function syncIndicator(status: SyncStatus): string | null {
  if (status.phase === "idle") {
    if (status.conflicts > 0) return `${status.conflicts} conflict ${status.conflicts === 1 ? "copy" : "copies"}`;
    if (status.parked > 0) return `${status.parked} waiting to apply`;
    return null;
  }
  if (status.phase === "blocked" || status.phase === "failed") return "Sync needs attention";
  if (status.phase === "offline" && status.pending > 0) return `Offline, ${changes(status.pending)} waiting`;
  if (status.phase === "syncing" && status.pending > 0) return "Syncing…";
  return null;
}
